import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleAuthService, GoogleUser } from '../../services/google-auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-google-signin',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="google-signin-container">
      <!-- Sign In Button -->
      <div 
        *ngIf="!isLoggedIn" 
        [id]="buttonId" 
        class="google-signin-button">
      </div>
      
      <!-- User Info when logged in -->
      <div *ngIf="isLoggedIn && user" class="user-info">
        <div class="user-profile">
          <img [src]="user.picture" [alt]="user.name" class="user-avatar">
          <div class="user-details">
            <span class="user-name">{{ getDisplayName() }}</span>
            <span class="user-email">{{ user.email }}</span>
          </div>
          <button 
            (click)="signOut()" 
            class="sign-out-btn"
            title="Sign Out">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
            </svg>
          </button>
        </div>
      </div>
      
      <!-- Loading state -->
      <div *ngIf="isLoading" class="loading-spinner">
        <div class="spinner"></div>
        <span>Signing in...</span>
      </div>
      
      <!-- Error state -->
      <div *ngIf="error" class="error-message">
        <span>{{ error }}</span>
        <button (click)="retry()" class="retry-btn">Retry</button>
      </div>
    </div>
  `,
  styles: [`
    .google-signin-container {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 50px;
    }

    .google-signin-button {
      min-width: 200px;
      min-height: 40px;
    }

    .user-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .user-profile {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 16px;
      background: #f5f5f5;
      border-radius: 24px;
      border: 1px solid #e0e0e0;
    }

    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
    }

    .user-details {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .user-name {
      font-weight: 500;
      font-size: 14px;
      color: #202124;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
    }

    .user-email {
      font-size: 12px;
      color: #5f6368;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
    }

    .sign-out-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      color: #5f6368;
      transition: all 0.2s ease;
    }

    .sign-out-btn:hover {
      background: #e8eaed;
      color: #202124;
    }

    .loading-spinner {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      font-size: 14px;
      color: #5f6368;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid #e0e0e0;
      border-top: 2px solid #1a73e8;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: #fce8e6;
      border: 1px solid #f28b82;
      border-radius: 4px;
      color: #d93025;
      font-size: 14px;
    }

    .retry-btn {
      background: #d93025;
      color: white;
      border: none;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      cursor: pointer;
      transition: background 0.2s ease;
    }

    .retry-btn:hover {
      background: #b31412;
    }

    @media (max-width: 768px) {
      .user-name,
      .user-email {
        max-width: 100px;
      }
    }
  `]
})
export class GoogleSigninComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() buttonId: string = 'google-signin-button';
  @Input() buttonOptions: any = {};
  @Input() autoNavigate: boolean = true;
  
  @Output() signInSuccess = new EventEmitter<GoogleUser>();
  @Output() signInError = new EventEmitter<any>();
  @Output() signOutSuccess = new EventEmitter<void>();

  isLoggedIn = false;
  user: GoogleUser | null = null;
  isLoading = false;
  error: string | null = null;

  private subscriptions: Subscription[] = [];

  constructor(private googleAuthService: GoogleAuthService) {}

  ngOnInit(): void {
    // Subscribe to auth state changes
    this.subscriptions.push(
      this.googleAuthService.authState$.subscribe(isLoggedIn => {
        const wasLoggedIn = this.isLoggedIn;
        this.isLoggedIn = isLoggedIn;
        this.isLoading = false;
        this.error = null;
        
        // If user just logged out (was logged in, now not), re-initialize the button
        if (wasLoggedIn && !isLoggedIn) {
          console.log('🔄 GoogleSigninComponent: User logged out, re-initializing sign-in button...');
          setTimeout(() => {
            this.initializeGoogleSignIn();
          }, 100); // Small delay to ensure DOM is updated
        }
      })
    );

    // Subscribe to user changes
    this.subscriptions.push(
      this.googleAuthService.user$.subscribe(user => {
        this.user = user;
        if (user) {
          this.signInSuccess.emit(user);
        }
      })
    );
  }

  async ngAfterViewInit(): Promise<void> {
    console.log('🚀 GoogleSigninComponent: ngAfterViewInit called, isLoggedIn:', this.isLoggedIn);
    
    if (!this.isLoggedIn) {
      console.log('🔄 GoogleSigninComponent: User not logged in, initializing Google Sign-In...');
      await this.initializeGoogleSignIn();
    } else {
      console.log('✅ GoogleSigninComponent: User already logged in, skipping initialization');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private async initializeGoogleSignIn(): Promise<void> {
    try {
      console.log('🔧 GoogleSigninComponent: Starting initialization...');
      this.isLoading = true;
      this.error = null;

      console.log('📡 GoogleSigninComponent: Calling googleAuthService.initializeGoogleSignIn()...');
      await this.googleAuthService.initializeGoogleSignIn();
      
      console.log('⏰ GoogleSigninComponent: Waiting 100ms for DOM to be ready...');
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        console.log('🎨 GoogleSigninComponent: Rendering button with ID:', this.buttonId);
        console.log('🔍 GoogleSigninComponent: Looking for element with ID:', this.buttonId);
        
        const buttonElement = document.getElementById(this.buttonId);
        if (buttonElement) {
          console.log('✅ GoogleSigninComponent: Button element found:', buttonElement);
          this.googleAuthService.renderSignInButton(this.buttonId, this.buttonOptions);
          console.log('🎯 GoogleSigninComponent: Button rendering completed');
        } else {
          console.error('❌ GoogleSigninComponent: Button element not found with ID:', this.buttonId);
        }
        
        this.isLoading = false;
      }, 100);
    } catch (error) {
      console.error('💥 GoogleSigninComponent: Failed to initialize Google Sign-In:', error);
      this.error = 'Failed to load Google Sign-In. Please check your internet connection.';
      this.signInError.emit(error);
      this.isLoading = false;
    }
  }

  signOut(): void {
    this.googleAuthService.logout();
    this.signOutSuccess.emit();
  }

  async retry(): Promise<void> {
    await this.initializeGoogleSignIn();
  }

  getDisplayName(): string {
    if (!this.user) return '';
    
    // Show full name if length < 16, otherwise show only first name
    return this.user.name.length >= 16 
      ? this.user.given_name 
      : this.user.name;
  }

  async manualSignIn(): Promise<void> {
    try {
      this.isLoading = true;
      this.error = null;
      await this.googleAuthService.signIn();
    } catch (error) {
      this.error = 'Sign-in failed. Please try again.';
      this.signInError.emit(error);
      this.isLoading = false;
    }
  }
} 